import { ReactElement, ReactNode, useMemo } from 'react';
import {
    LocationAnalyticsContext,
    LocationAnalyticsContextData
} from './context';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '@/tasks/providers';
import { useFormatDate, useTimeSettings } from '@/common/hooks';
import {
    getEndOfDayInTimezone,
    getStartOfDayInTimezone
} from '@/common/util/format-date';
import { useFetchLocationAnalytics } from '../../queries';
import { buildAnalyticsQueryFilter } from '../../util/analytics-query-filter';
import { toFeatureCollection } from '../../util/to-feature-collection';
import { useReportsFilter } from '../reports-filter-provider';

// Location points are only retained for this long, so a report reaching
// further back is clamped rather than silently drawn short.
const MAX_DAYS_AGO = 30;

type LocationAnalyticsProviderProps = {
    readonly children?: ReactNode;
};

export function LocationAnalyticsProvider({
    children
}: LocationAnalyticsProviderProps): ReactElement {
    const { range } = useReportsFilter();
    const { timezone } = useTimeSettings();

    const adjustedDateRange = useMemo<{
        from: string;
        to: string;
        isAdjusted: boolean;
    } | null>(() => {
        let isAdjusted = false;

        const oldestFrom = new Date();
        oldestFrom.setDate(oldestFrom.getDate() - MAX_DAYS_AGO);
        const newestTo = new Date();

        // Get timezone-aware boundaries for the oldest allowed date and today
        const oldestFromISO = getStartOfDayInTimezone(oldestFrom, timezone);
        const newestToISO = getEndOfDayInTimezone(newestTo, timezone);

        // Get timezone-aware boundaries for the query range
        const queryFromISO = getStartOfDayInTimezone(range.from, timezone);
        const queryToISO = getEndOfDayInTimezone(range.to, timezone);

        // Compare as ISO strings (lexicographically sortable)
        if (queryToISO < oldestFromISO || queryFromISO > newestToISO) {
            return null; // No valid date range
        }

        const from =
            queryFromISO < oldestFromISO ? oldestFromISO : queryFromISO;
        const to = queryToISO > newestToISO ? newestToISO : queryToISO;

        if (from !== queryFromISO || to !== queryToISO) {
            isAdjusted = true;
        }

        return { from, to, isAdjusted };
    }, [range, timezone]);

    if (adjustedDateRange) {
        return (
            <ValidDateRangeInnerProvider dateRange={adjustedDateRange}>
                {children}
            </ValidDateRangeInnerProvider>
        );
    } else {
        return (
            <LocationAnalyticsContext.Provider
                value={{
                    status: 'error',
                    featureCollection: null,
                    progress: 0,
                    dateString: null,
                    isWithinDateRange: false,
                    isDateRangeAdjusted: false,
                    maxDaysAgoAllowed: MAX_DAYS_AGO
                }}
            >
                {children}
            </LocationAnalyticsContext.Provider>
        );
    }
}

function ValidDateRangeInnerProvider(props: {
    dateRange: { from: string; to: string; isAdjusted: boolean };
    readonly children?: ReactNode;
}): ReactElement {
    const { dateRange, children } = props;
    const { company } = useCompanyContext();
    const { teamIds, userIds, deviceIds } = useTaskFilter();
    const { formatDateRange } = useFormatDate();
    const { timezone } = useTimeSettings();

    const query = useMemo(() => {
        return buildAnalyticsQueryFilter({
            companyId: company.id,
            teamIds,
            userIds,
            deviceIds,
            timezone,
            from: dateRange.from,
            to: dateRange.to
        });
    }, [company.id, teamIds, userIds, deviceIds, dateRange, timezone]);

    const { data, status, progress } = useFetchLocationAnalytics(query);

    const featureCollection = useMemo(() => {
        if (!data) {
            return null;
        }
        return toFeatureCollection(data.coordinates);
    }, [data]);

    const dateString = useMemo(() => {
        return formatDateRange(
            new Date(dateRange.from),
            new Date(dateRange.to)
        );
    }, [dateRange, formatDateRange]);

    const value: LocationAnalyticsContextData = {
        status,
        featureCollection,
        progress,
        dateString,
        isWithinDateRange: true,
        isDateRangeAdjusted: dateRange.isAdjusted,
        maxDaysAgoAllowed: MAX_DAYS_AGO
    };

    return (
        <LocationAnalyticsContext.Provider value={value}>
            {children}
        </LocationAnalyticsContext.Provider>
    );
}
